import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $initialCurrenciesAvailableStore, $monthlyTransactionStore } from '@lib/store';
import { cn, DateUtils, PaletteUtils } from '@shared/utils';
import type { PaymentSummariesResponse } from '@shared/api';
import { TransactionDirection } from '@shared/api';
import { TransactionItem } from './transactionItem';
// import { RequestTransaction } from './requestTransaction';
import { View } from '@widgets';
import { Select, Tabs, Text } from '@shared/widgets';

const CURRENT_MONTH_INDEX = dayjs().month() + 1;
const CURRENT_YEAR = dayjs().year();

export interface MonthlyTransactionProps {
  minimalYear: number;
  minimalMonth: number;
}

export const MonthlyTransaction = (props: MonthlyTransactionProps) => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const payment = useUnit($monthlyTransactionStore.store);

  const { t, i18n } = useTranslation('dashboard');

  const yearsList = useMemo(() => DateUtils.getYearsList(props.minimalYear), [props.minimalYear]);
  const currentMonth = DateUtils.getCurrentMonth({ locale: i18n.language });
  const [selectedYear, setSelectedYear] = useState<string>(yearsList[yearsList.length - 1]);
  const monthsList = useMemo(
    () =>
      DateUtils.getMonthsSelectList({
        locale: i18n.language,
        minimalMonth: +selectedYear! === props.minimalYear ? props.minimalMonth : 1,
        maximalMonth: +selectedYear! === CURRENT_YEAR ? CURRENT_MONTH_INDEX : 12,
      }),
    [i18n.language, props.minimalYear, props.minimalMonth, selectedYear],
  );
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>(monthsList.find((month) => month.label === currentMonth)?.value);
  const [currency, setCurrency] = useState<string>(currenciesAvailableStore.data[0]);
  const [tab, setTab] = useState<number>(1);

  useEffect(() => {
    if (!currency) return;

    $monthlyTransactionStore.events.getPaymentSummary({
      Currency: currency,
      DateFrom: DateUtils.getFirstDateOfYear(selectedYear, selectedMonth),
      DateTo: DateUtils.getLastDateOfYear(selectedYear, selectedMonth),
    });

    return () => $monthlyTransactionStore.events.clear();
  }, [currency, selectedYear, selectedMonth]);

  const tabs = useMemo(
    () => [
      { label: t('payment.tabs.outgoingTransactions'), value: 1 },
      { label: t('payment.tabs.incomingTransactions'), value: 2 },
      // { label: t('payment.tabs.requestOutgoingSummary'), value: 3 },
    ],
    [t],
  );
  const content = useMemo(() => {
    switch (tab) {
      case 1:
        return (
          <TransactionItem
            {...(payment.data as PaymentSummariesResponse).outgoingTransactions}
            itemType={TransactionDirection.Outgoing}
            currency={currency}
            textValue={`${monthsList.find((month) => month.value === selectedMonth)?.label} ${selectedYear!}`}
          />
        );
      case 2:
        return (
          <TransactionItem
            {...(payment.data as PaymentSummariesResponse).incomingTransactions}
            itemType={TransactionDirection.Incoming}
            currency={currency}
            textValue={`${monthsList.find((month) => month.value === selectedMonth)?.label} ${selectedYear!}`}
          />
        );
      // case 3:
      //   return (
      //     <RequestTransaction
      //       {...(payment.data as PaymentSummariesResponse).outgoingTransactionSummary}
      //       currency={currency!}
      //       textValue={`${monthsList.find((month) => month.value === selectedMonth)?.label} ${selectedYear!}`}
      //     />
      //   );
      default:
        return null;
    }
  }, [tab, currency, monthsList, payment.loading, payment.data]);
  return (
    <div
      className={cn(
        PaletteUtils.isInsigniaPalette && 'flex flex-col gap-6',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
      )}
    >
      <div className={cn('flex items-center justify-between', PaletteUtils.isVcfcPalette && ['p-6 pb-0', 'max-md:flex-col max-md:items-start'])}>
        <Text className='mr-16' weight={PaletteUtils.isVcfcPalette ? 'semibold' : undefined} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
          {t('payment.monthlyTransaction.title')}
        </Text>
        <div className={cn('flex gap-6', PaletteUtils.isVcfcPalette && 'max-md:mt-3 max-md:w-full max-md:flex-col')}>
          <Select className='min-w-[100px]' items={currenciesAvailableStore.data} value={currency} onChange={(value) => setCurrency(value!)} />
          <Select className='min-w-[130px]' items={monthsList} value={selectedMonth} onChange={setSelectedMonth} />
          <Select items={yearsList} value={selectedYear} onChange={(value) => setSelectedYear(value!)} />
        </div>
      </div>
      <View
        loadingClassName={cn(PaletteUtils.isVcfcPalette && 'pb-6 pt-4')}
        errorClassName={cn(PaletteUtils.isVcfcPalette && 'p-6 pt-4')}
        noDataClassName={cn(PaletteUtils.isVcfcPalette && 'p-6 pt-4')}
        loading={payment.loading}
        error={payment.error}
        onRefresh={() =>
          $monthlyTransactionStore.events.getPaymentSummary({
            Currency: currency,
            DateFrom: DateUtils.getFirstDateOfYear(selectedYear, selectedMonth),
            DateTo: DateUtils.getLastDateOfYear(selectedYear, selectedMonth),
          })
        }
      >
        <div className={cn('relative flex', PaletteUtils.isVcfcPalette && ['pl-2', 'max-md:no-scrollbar max-md:overflow-x-auto max-md:pb-4'])}>
          <Tabs className='w-full p-4' options={tabs} value={tab} onChange={setTab} />
        </div>
        {content}
      </View>
    </div>
  );
};
